/// <amd-module name="Test/FailedTestsMetricView/Sources/FailedTestsMetricSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { TestReportSource } from "Test/Analytics/Common/Sources/TestReportSource";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { DataSourceHelper } from "Test/Analytics/Common/Utils/DataSourceHelper";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";

export class FailedTestsMetricSource extends TestReportSource {
    constructor(pageContext: IVssPageContext) {
        super("TestResultsAnalytics_InContext_FailedTestsMetricReport", pageContext);
    }

    public queryTotalFailingTestsMetricData(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<CommonTypes.IFailedCardMetricData> {
        const sourceTable: CommonTypes.DataSourceTable = DataSourceHelper.getFailingTestCardDataSourceTable(confValues);
        if (sourceTable === CommonTypes.DataSourceTable.TestResult) {
            return this._queryFailingMetricData_TestResultTable(testReportContext, confValues, false);
        } else {
            return this._queryFailingMetricData_TestResultDailyTable(testReportContext, confValues, false);
        }
    }

    public queryTotalFlakyTestsMetricData(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<CommonTypes.IFailedCardMetricData> {
        const sourceTable: CommonTypes.DataSourceTable = DataSourceHelper.getFailingTestCardDataSourceTable(confValues);
        if (sourceTable === CommonTypes.DataSourceTable.TestResult) {
            return this._queryFailingMetricData_TestResultTable(testReportContext, confValues,true);
        } else {
            return this._queryFailingMetricData_TestResultDailyTable(testReportContext, confValues,true);
        }
    }
    
    private _queryFailingMetricData_TestResultDailyTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration,
        IsFlakyScanerio: boolean = false  // adding IsFlakyScanerio flag to handle flaky scanerio specific query 
    ): Promise<CommonTypes.IFailedCardMetricData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultDailyEntityName,
                project: projectId
            } as ODataQueryOptions;
            this._getTestRunTitlePropertyString = () => {
                return "TestRun/Title";
            };
            const filterStr = this._testResultDaily_filterStringBuilder(testReportContext, confValues, queryOptions, IsFlakyScanerio);
            return this._queryTotalFailingTestsMetricData(queryOptions, filterStr, confValues);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }
    
    private _testResultDaily_filterStringBuilder(  //utility function two build filterString based on scanerio for TestResultDaily table
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration,
        queryOptions: ODataQueryOptions,
        IsFlakyScanerio: boolean = false
    )
    {
        const groupByStr = `groupby((TestSK))`;
        const aggrStr = `aggregate($count as TotalFailingTestsCount)`;
        var filterStr =
                `filter(${this._getTestResultsContextPipelineFilter(testReportContext)} and ${this._getDailyDateFilter(
                    confValues.trendBy,
                    confValues.period
                )}` +
                (IsFlakyScanerio ? ``: ` and ${this._getOutcomeCountFilter(CommonTypes.TestOutcome.Failed)}`) +
                ` ${this._getFilterByFieldsString(confValues.configuredFilters)})`;
        if(!IsFlakyScanerio){
            queryOptions.$apply = `${filterStr}/${groupByStr}/${aggrStr}`;
        }
        else{
            filterStr = filterStr +
               `/groupby((TestSK),aggregate(ResultFlakyCount with sum as TotalFlakyCount, ResultFailCount with sum as TotalFailCount))`+
               `/filter(TotalFlakyCount gt 0 and TotalFailCount gt 0)`;
            queryOptions.$apply = `${filterStr}/${aggrStr}`;
        }
        return filterStr
    }

    private _queryFailingMetricData_TestResultTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration,
        IsFlakyScanerio: boolean = false // adding IsFlakyScanerio flag to handle flaky scanerio specific query 
    ): Promise<CommonTypes.IFailedCardMetricData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultEntityName,
                project: projectId
            } as ODataQueryOptions;

            this._getTestRunTitlePropertyString = () => {
                return "TestRun/Title"; 
            };
            const filterStr = this._testResult_filterStringBuilder(testReportContext, confValues, queryOptions, IsFlakyScanerio);
            return this._queryTotalFailingTestsMetricData(queryOptions, filterStr, confValues);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _testResult_filterStringBuilder( //utility function two build filterString based on scanerio for TestResult table
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration,
        queryOptions: ODataQueryOptions,
        IsFlakyScanerio: boolean = false
    )
    {
        const groupByStr = `groupby((TestSK))`;
        const aggrStr = `aggregate($count as TotalFailingTestsCount)`;
        var filterStr =
                `filter(${this._getTestResultsContextPipelineFilter(testReportContext)} and ${this._getDateFilter(
                    confValues.trendBy,
                    confValues.period
                )}` +
                (IsFlakyScanerio ? ``: ` and ${this._getOutcomeFilter(CommonTypes.TestOutcome.Failed)}`) +
                ` ${this._getFilterByFieldsString(confValues.configuredFilters)})`;
        if(!IsFlakyScanerio){
            queryOptions.$apply = `${filterStr}/${groupByStr}/${aggrStr}`;
        }
        else{
            filterStr = filterStr +
                `/groupby((TestSK),aggregate(cast(IsFlaky, Edm.Int64)with sum as TotalFlakyCount, cast(Outcome eq 'Failed', Edm.Int64) with sum as TotalFailCount))`+
                `/filter(TotalFlakyCount gt 0 and TotalFailCount gt 0)`;
            queryOptions.$apply = `${filterStr}/${aggrStr}`;
        }
        return filterStr
    }

    private _queryTotalFailingTestsMetricData(
        queryOptions: ODataQueryOptions,
        filterStr: string,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<CommonTypes.IFailedCardMetricData> {
        
        return this.queryOData(queryOptions).then((data: CommonTypes.IODataQueryResponse) => {
            if (!data || !data.value) {
                throw new Error("Data is empty");
            }

            const metricsData: CommonTypes.IFailedCardMetricData = {
                totalFailingTestsCount: 0,
                confValues: confValues,
                isCachedData: data.isCachedData
            } as CommonTypes.IFailedCardMetricData;

            // Iterate over all data. There should be only item in list as we are fetching aggregates and not group.
            (data.value as any[]).forEach(d => {
                metricsData.totalFailingTestsCount = d.TotalFailingTestsCount as number;
            });

            return metricsData;
        });
    }
}

